import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { first, Subject, takeUntil } from 'rxjs';
import { ActionsModalComponent } from 'src/app/shared/actions-modal/actions-modal.component';
import { RtsDay, RtsVehicle } from 'src/app/dto/get-rts-result.dto';
import { HeaderDto } from '../../../dto/header.dto';
import { Order } from '../../../dto/order.dto';
import { Status } from '../../../dto/status.enum';
import { OrderChangesService } from '../../../services/order/order-changes.service';
import { OrderProviderService } from '../../../services/order/order-provider.service';
import { DocTypes, FileService } from '../../../services/file.service';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular';
import { FilesHelperService } from '../../../services/files-helper.service';

@Component({
  selector: 'app-day-details',
  templateUrl: './day-details.component.html',
  styleUrls: ['./day-details.component.scss']
})
/**
 * Экран одного дня заявки: список машин (рейсов) с их данными погрузки, старт и завершение дня,
 * скачивание расписки экспедитора. Состояние экрана целиком пересобирается из локальной версии заказа
 * ({@link #dataUpdate}) на каждой эмиссии из потока — поэтому правки и синхронизация видны сразу.
 * Переход к вводу погрузки ({@code load-car}) доступен только для НАЧАТОГО дня (или архивной заявки).
 */
export class DayDetailsComponent implements OnInit, OnDestroy {
  headerDataValues: HeaderDto;
  loading: boolean;
  dayDetails: RtsDay;
  order: Order;
  carsList: RtsVehicle[];
  isDayStarted: boolean;
  isRequestInWork: boolean;
  currentDayId: string = '';
  disableDayStartSwipe: boolean = false;
  disableDayEndButton: boolean = false;
  dayNumber: number;
  targetUrl = window.location.pathname.split('/')[1];
  orderId: number;
  defaultBackUrl: string;
  isDownloading: boolean = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private orderChangesService: OrderChangesService,
    private cd: ChangeDetectorRef,
    private readonly orderProvider: OrderProviderService,
    private fileService: FileService,
    private filesHelper: FilesHelperService
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(first()).subscribe(parameter => {
      this.currentDayId = parameter['id'];
    });
    this.defaultBackUrl = window.location.pathname
      .split('/')
      .slice(0, -2)
      .join('/');
    this.orderId = this.route.snapshot.parent?.parent?.params['id'];
    this.orderProvider
      .loadOrder(this)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async success => {
        if (success) {
          this.dataUpdate();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Пересобирает всё состояние экрана из текущей версии заказа: суммарный вес (нетто по машинам, а для
   * заявки в статусе {@code OPEN} — плановый 25 000 кг на машину), список машин, номер дня, шапку,
   * признаки «день начат» и «заявка в работе». Вызывается на каждой эмиссии заказа, поэтому производные
   * флаги ниже пересчитываются с чистого листа.
   */
  dataUpdate() {
    let totalAmount = 0;

    const dayData = this.order.getDayById(this.currentDayId);

    if (dayData) {
      this.dayDetails = dayData;

      if (this.order.status !== Status.OPEN) {
        this.dayDetails.vehicles.forEach((vehicle: RtsVehicle) => {
          if (vehicle?.loadingData?.netWeight) {
            totalAmount += vehicle.loadingData.netWeight;
          }
        });
      } else {
        totalAmount = this.dayDetails.vehicles.length * 25000;
      }
      this.carsList = [...dayData.vehicles];
      this.dayNumber = this.order.getDayNumberById(dayData.orderNumber);
      this.isDayStarted = dayData.started;
      this.headerDataValues = {
        title: `День ${this.dayNumber}, РТС`,
        subtitle: {
          cars: dayData.vehicles.length,
          amount: totalAmount
        },
        options: !this.order.status.includes(Status.OPEN),
        carsData: this.carsList,
        orderData: this.order
      };
    }

    this.isRequestInWork =
      this.order.status.includes(Status.ACCEPTED) ||
      this.order.status.includes(Status.IN_WORK);

    this.checkDayStartOptions();
    this.checkDayEndOptions();
  }

  /**
   * Старт дня по свайпу. День начинается, только если его можно начать ({@link Order#canStartDay}): заявка в
   * работе и перевозчик назначен — начатый день означает начало перевозки. Иначе показываем окно с причиной
   * и откатываем переключатель: при {@code ACCEPTED} не подписано поручение экспедитору, при {@code IN_WORK}
   * перевозчик ещё не взял заявку.
   */
  dayStart(event: boolean) {
    if (!event) {
      return;
    }
    if (this.order.canStartDay()) {
      const updatedRequest = this.order.startDay(this.dayDetails.orderNumber);
      this.orderChangesService.updateRequests(updatedRequest, this.targetUrl);
      this.isDayStarted = true;
      return;
    }
    const reason =
      this.order.status === Status.ACCEPTED
        ? this.cantStartDayModal()
        : this.order.status === Status.IN_WORK
          ? this.carrierNotAssignedModal()
          : Promise.resolve();
    reason.then(() => {
      this.isDayStarted = false;
      this.cd.markForCheck();
    });
  }

  async cantStartDayModal() {
    await this.showCannotStartDay(
      'Необходимо подписать поручение экспедитору по заявке',
      'Чтобы начать работу с заявкой, необходимо подписать поручение экспедитору в системе ЭДО'
    );
  }

  /**
   * Перевозчик не подтверждён. Сервер прямо сказал «не назначен» — так и пишем; сведений нет (без связи, сервис
   * перевозок не ответил, заявка на перевозку не опубликована) — не утверждаем того, чего не знаем.
   */
  async carrierNotAssignedModal() {
    if (this.order.carrierAssignmentConfirmed === false) {
      await this.showCannotStartDay(
        'Перевозчик ещё не назначен',
        'Начать день погрузки можно после того, как перевозчик возьмёт заявку.'
      );
      return;
    }
    await this.showCannotStartDay(
      'Нет подтверждения, что перевозчик назначен',
      'Начать день погрузки можно, когда перевозчик назначен. Обновите заявку при подключении к сети — ' +
        'приложение получит сведения о перевозчике. Если сведений нет и после обновления, обратитесь к агенту.'
    );
  }

  async dayEnd() {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          confirmButton: true,
          confirmButtonText: 'Завершить',
          message:
            'Не завершайте день, если не загружены все автомобили из списка РТС.',
          title: 'Хотите завершить день?',
          imageSrc: '../../../assets/images/end-day-car.svg'
        }
      }
    });
    modal.present();

    const { role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      this.navCtrl.navigateForward(`${window.location.pathname}/end-day`);
    }
  }


  checkDayStartOptions() {
    // Пересчитываем с чистого листа: метод вызывается на каждой эмиссии заказа, и без сброса флаг,
    // однажды выставленный в true, залипал бы даже после того, как условие перестало действовать
    // (кнопка «Начать день» оставалась бы заблокированной без причины).
    this.disableDayStartSwipe = false;
    this.order.registry?.days.forEach(day => {
      if (
        (day.started &&
          !day.completed &&
          day.orderNumber.replace(/\|/g, '') !== this.currentDayId) ||
        this.order.status === Status.ACCEPTED
      ) {
        this.disableDayStartSwipe = true;
      }
    });
  }

  /**
   * Блокирует завершение дня, если у какой-либо машины НАЧАТА погрузка (есть вес нетто), но заполнены не
   * все обязательные поля (номер/дата карантинного сертификата, СДИЗ, номер/дата декларации, тара,
   * брутто). Условие совпадает с {@link #hasIncompleteLoadingData} (подсветка отдельной машины).
   */
  checkDayEndOptions() {
    if (
      !this.dayDetails ||
      !this.dayDetails.vehicles ||
      this.dayDetails.vehicles.length === 0
    ) {
      this.disableDayEndButton = false;
      return;
    }

    const hasUnfilledFields = this.dayDetails.vehicles.some(vehicle => {
      if (vehicle.loadingData) {
        const loadingData = vehicle.loadingData;
        const hasWeight =
          loadingData.netWeight !== null && loadingData.netWeight !== 0;

        const hasMissingFields =
          loadingData.quarantineCertificateNumber === null ||
          loadingData.sdizNumber === null ||
          loadingData.quarantineCertificateDate === 0 ||
          loadingData.declarationOfConformityDate === 0 ||
          loadingData.tareWeight === null ||
          loadingData.tareWeight === 0 ||
          loadingData.grossWeight === null ||
          loadingData.grossWeight === 0 ||
          loadingData.declarationOfConformityNumber === null;

        return hasWeight && hasMissingFields;
      }
      return false;
    });

    this.disableDayEndButton = hasUnfilledFields;
  }

  hasIncompleteLoadingData(vehicle: RtsVehicle): boolean {
    if (vehicle.loadingData) {
      const loadingData = vehicle.loadingData;
      const hasWeight =
        loadingData.netWeight !== null && loadingData.netWeight !== 0;

      const hasMissingFields =
        loadingData.quarantineCertificateNumber === null ||
        loadingData.sdizNumber === null ||
        loadingData.quarantineCertificateDate === 0 ||
        loadingData.declarationOfConformityDate === 0 ||
        loadingData.tareWeight === null ||
        loadingData.tareWeight === 0 ||
        loadingData.grossWeight === null ||
        loadingData.grossWeight === 0 ||
        loadingData.declarationOfConformityNumber === null;

      return hasWeight && hasMissingFields;
    }
    return false;
  }

  /**
   * Переход к вводу данных погрузки машины. Доступен, только если день начат или заявка архивная
   * ({@code COMPLETED}/{@code CLOSED}, режим просмотра); иначе показываем окно «начните день».
   * {@code carIndex} передаётся в query — для {@code load-car} это признак корректного перехода (в номере
   * ТТН он больше не участвует, D10).
   */
  openCarDetails(car: RtsVehicle, carIndex: number) {
    if (
      this.dayDetails.started ||
      this.order.status === Status.COMPLETED ||
      this.order.status === Status.CLOSED
    ) {
      const navigationExtras: NavigationExtras = {
        queryParams: {
          carIndex
        }
      };
     void this.navCtrl.navigateForward(
        `${window.location.pathname}/load-car/${this.currentDayId}/${car.id}`,
        navigationExtras
      );
    } else {
      this.startDayModal();
    }
  }

  async downloadExpeditorNote() {
    if (this.isDownloading) {
      return;
    }

    try {
      this.isDownloading = true;
      const fileName = `Расписка экспедитора-${this.order.contractName}.pdf`;
      const filePath = await this.fileService.downloadFileForDeal(
        Number(this.dayDetails.forwarderNoteDocumentId),
        fileName,
        DocTypes.FORWARDER_RECEIPT
      );

      // Открываем файл только на нативной платформе (по непустому пути).
      if (filePath && Capacitor.isNativePlatform()) {
        await this.filesHelper.openFile(filePath, 'application/pdf');
      }
      // В web файл уже скачан внутри downloadFileForDeal.
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.isDownloading = false;
    }
  }

  onToggleChange(event: CustomEvent<{ checked: boolean }>, index: number) {
    const checked = event.detail.checked;
    const vehicleId = this.carsList?.[index]?.id?.toString();
    if (!vehicleId) {
      return;
    }


    try {
      // Обновляем конкретную машину через методы Order по её ID
      this.order.editVehiclesBaseData(this.currentDayId, vehicleId, { isCarAtLoadingSite: checked });

      // Обновляем отображаемый список из актуальных данных заказа
      const day = this.order.getDayById(this.currentDayId);
      this.carsList = day?.vehicles ? [...day.vehicles] : this.carsList;

      // Сохраняем изменение и ставим в очередь синхронизацию бэкапа
      this.orderChangesService.updateRequests(this.order, this.targetUrl);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  async startDayModal() {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          message:
            'Для того, что бы заполнять информацию по отгрузке, добавлять или изменять параметры, необходимо начать день.',
          title: 'Для работы с машиной необходимо начать день!',
          imageSrc: '../../../assets/images/error-picture.svg',
          closeButton: true,
          confirmButton: !this.disableDayStartSwipe && this.isRequestInWork,
          confirmButtonText: 'Начать день'
        }
      }
    });
    modal.present();

    const { role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      // Признак начатого дня выставляет сам dayStart — и только если день действительно начат.
      this.dayStart(true);
    }
  }

  private async showCannotStartDay(title: string, message: string) {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: { title, message }
      }
    });
    await modal.present();
  }
}
